import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Content from './Content'
import MyTask from './MyTask'
import FabButton from './FabButton'
import { useUser } from '../../auth'
import UpcomingContentModal from '../modals/UpcomingContentModal'
import ReviewContentModal from '../modals/ReviewContentModal'
import { getNumberOfContentForReview, getUpcomingNum } from '../../server/server'
import { usePageTitle } from '../../utils/util'
import useCachedRequest from '../../hooks/useCachedRequest'
import useMediaQuery, { XL_QUERY } from '../../hooks/useMediaQuery'
import { oneOfCodec, useUrlState } from '../../hooks/useUrlState'
import '../../styles/dashboard/dashboardMotion.css'

const assertOk = (resp) => {
    if (resp?.status !== 200) throw new Error(`Unexpected status ${resp?.status}`)
    return resp.data
}
const fetchUpcomingNum = () => getUpcomingNum().then((resp) => Number(assertOk(resp)) || 0)
const fetchReviewNum = () => getNumberOfContentForReview().then((resp) => assertOk(resp)?.length ?? 0)

const PRIMARY_TABS = ['overview', 'feed']
const FULL_TABS = ['overview', 'contentResults', 'feed']

function Dashboard() {
    const { t } = useTranslation()
    usePageTitle('Upstrive Education - Dashboard')
    const { user } = useUser()

    // Above `xl` the My Tasks widget is a fixed right column; below it, a tab.
    // Deciding here (instead of CSS hiding both) means exactly one instance
    // mounts and fetches at any width.
    const isWide = useMediaQuery(XL_QUERY)
    const contentList = useMemo(() => {
        if (user?.hasOnlyPrimaryStudents) return PRIMARY_TABS
        return isWide ? FULL_TABS : [...FULL_TABS, 'myTask']
    }, [user?.hasOnlyPrimaryStudents, isWide])

    const [tab, setTab] = useUrlState(
        'tab',
        'overview',
        oneOfCodec(() => contentList)
    )
    // Guards against a ?tab= that is valid in general but not for this user or
    // width -- including the case where `user` had not resolved when the param
    // was parsed.
    const activeTab = contentList.includes(tab) ? tab : 'overview'
    useEffect(() => {
        if (tab !== activeTab) setTab(activeTab)
    }, [tab, activeTab, setTab])

    const isAbleToDoReview = useMemo(() => !!(user?.contentSettings?.studentContentReviewer || user?.contentSettings?.staffContentReviewer), [user])

    // Separate keys so `user` resolving only (re)fetches the review count.
    const upcoming = useCachedRequest('dashboard:upcomingNum', fetchUpcomingNum, { fallback: 0 })
    const review = useCachedRequest('dashboard:reviewNum', fetchReviewNum, { enabled: isAbleToDoReview, fallback: 0 })
    const { reload: reloadUpcoming } = upcoming
    const { reload: reloadReview } = review
    const reloadCounts = useCallback(() => {
        reloadUpcoming()
        reloadReview()
    }, [reloadUpcoming, reloadReview])

    const [showUpcomingContentModal, setShowUpcomingContentModal] = useState(false)
    const [showReviewContentModal, setShowReviewContentModal] = useState(false)
    const openUpcoming = useCallback(() => setShowUpcomingContentModal(true), [])
    const closeUpcoming = useCallback(() => setShowUpcomingContentModal(false), [])
    const openReview = useCallback(() => setShowReviewContentModal(true), [])
    const closeReview = useCallback(() => setShowReviewContentModal(false), [])

    // The task list has its own actions; the floating triggers would sit on top of it.
    const triggersHidden = activeTab === 'myTask'
    const upcomingCount = upcoming.data ?? 0
    const reviewCount = review.data ?? 0

    return (
        <div className="relative h-full">
            <div className="flex justify-center bg-[#ECEBF8] h-full overflow-y-scroll px-2 pb-2">
                <div className="dashboard-layout flex flex-row gap-[35px] py-10 w-full max-w-[1380px] mx-auto h-fit min-h-full">
                    <div className="dashboard-left-side flex-1 xxs:max-w-[96%] xl:max-w-[865px] mx-auto">
                        <Content activeTab={activeTab} setActiveTab={setTab} contentList={contentList} />
                    </div>
                    {isWide && (
                        <div>
                            <MyTask user={user} />
                        </div>
                    )}
                </div>
            </div>

            <div className="dsh-fab-row" data-hidden={triggersHidden} aria-hidden={triggersHidden}>
                {isAbleToDoReview && <FabButton id="review-content-fab" label={t('Review content')} count={reviewCount} loading={review.loading} onClick={openReview} />}
                <FabButton id="upcoming-content-fab" label={t('upcomingContent')} count={upcomingCount} loading={upcoming.loading} onClick={openUpcoming} />
            </div>

            <UpcomingContentModal isOpen={showUpcomingContentModal} onClose={closeUpcoming} count={upcomingCount} onCountChanged={reloadCounts} />
            {isAbleToDoReview && <ReviewContentModal isOpen={showReviewContentModal} onClose={closeReview} count={reviewCount} onCountChanged={reloadCounts} />}
        </div>
    )
}

export default Dashboard
